import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "../styles/components/LanguageSwitcher.css";

export const LANGUAGE_OPTIONS = [
  {
    code: "bg",
    triggerLabel: "BG",
    nativeLabel: "Български",
    flag: "🇧🇬",
    order: 1,
  },
  {
    code: "de",
    triggerLabel: "DE",
    nativeLabel: "Deutsch",
    flag: "🇩🇪",
    order: 2,
  },
  {
    code: "el",
    triggerLabel: "EL",
    nativeLabel: "Ελληνικά",
    flag: "🇬🇷",
    order: 3,
  },
  {
    code: "en",
    triggerLabel: "EN",
    nativeLabel: "English",
    flag: "🇬🇧",
    order: 4,
  },
  {
    code: "es",
    triggerLabel: "ES",
    nativeLabel: "Español",
    flag: "🇪🇸",
    order: 5,
  },
  {
    code: "fr",
    triggerLabel: "FR",
    nativeLabel: "Français",
    flag: "🇫🇷",
    order: 6,
  },
  {
    code: "pl",
    triggerLabel: "PL",
    nativeLabel: "Polski",
    flag: "🇵🇱",
    order: 7,
  },
  {
    code: "pt",
    triggerLabel: "PT",
    nativeLabel: "Português",
    flag: "🇵🇹",
    order: 8,
  },
  {
    code: "tr",
    triggerLabel: "TR",
    nativeLabel: "Türkçe",
    flag: "🇹🇷",
    order: 9,
  },
];

const DEFAULT_LANGUAGE = LANGUAGE_OPTIONS.find(({ code }) => code === "en");

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, inlineEnd: 12 });
  const switcherRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const activeLang = (i18n.resolvedLanguage || i18n.language || "en").split(
    "-"
  )[0];
  const currentLang =
    LANGUAGE_OPTIONS.find(({ code }) => code === activeLang) || DEFAULT_LANGUAGE;

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 6,
      inlineEnd: Math.max(12, window.innerWidth - rect.right),
    });
  }, []);

  const changeLang = (code) => {
    const nextCode = LANGUAGE_OPTIONS.some((language) => language.code === code)
      ? code
      : "en";
    i18n.changeLanguage(nextCode);
    localStorage.setItem("i18nextLng", nextCode);
    setOpen(false);
  };

  const toggleMenu = () => {
    if (!open) updateDropdownPosition();
    setOpen((current) => !current);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (
        switcherRef.current &&
        !switcherRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open, updateDropdownPosition]);

  return (
    <div className="language-switcher" ref={switcherRef}>
      <button
        ref={buttonRef}
        type="button"
        className="language-switcher-button"
        aria-label={`Language: ${currentLang.nativeLabel}`}
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggleMenu}
      >
        <span className="lang-code">{currentLang.triggerLabel}</span>
        <span className="lang-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open
        ? createPortal(
            <div
              id={menuId}
              ref={dropdownRef}
              className="language-switcher-dropdown"
              role="menu"
              aria-label="Languages"
              style={{
                top: dropdownPos.top,
                insetInlineEnd: dropdownPos.inlineEnd,
                "--language-switcher-menu-top": `${dropdownPos.top}px`,
              }}
            >
              {LANGUAGE_OPTIONS.map((language) => {
                const isActive = language.code === currentLang.code;

                return (
                  <button
                    key={language.code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    aria-label={language.nativeLabel}
                    data-locale={language.code}
                    className={`language-switcher-option${
                      isActive ? " language-switcher-option--active" : ""
                    }`}
                    onClick={() => changeLang(language.code)}
                  >
                    <span className="lang-flag" aria-hidden="true">
                      {language.flag}
                    </span>
                    <span className="language-switcher-native-name">
                      {language.nativeLabel}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
